// ---------------------------------------------------------------------------
// UX-020 — Trust badge catalog + per-tenant resolver (pure data layer)
// ---------------------------------------------------------------------------
//
// Canonical surface specified in
// `docs/vision_execution/openclaw/UX-020-DECISION-PACKET.md` §"Minimal viable
// scope" Phase 1 + Phase 2 (data half).
//
// This module is the pure-data foundation that Phase 2 (`resolveTrustBadges`
// service + control-plane route) and Phase 1 (`<TrustBadges>` React
// primitive in `@ease/ui`) both depend on. Keeping it in `@ease/shared`
// means workers can resolve a badge set without touching React/UI, and the
// upcoming Puck `trust-badges` page-builder component (Phase 4) can reuse
// the same filter logic — which simultaneously closes the dangling type
// reference at `packages/page-builder/src/services/conversion-scorer.ts:197`
// (`hasBadges` heuristic that today scores +0.3 for a never-rendered
// component type).
//
// No DOM, no React, no DB driver. Safe for CF Workers, Bun, Node, or
// browser bundles.

import { z } from "zod";

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

/**
 * The 9 canonical V1 trust-signal ids drawn from the decision packet
 * §"What UX-020 requires" point 1. Locked at the type level so adding a
 * new badge requires touching this module + the catalog + the resolver
 * tests in one PR (no drift).
 */
export const TRUST_BADGE_IDS = [
  "gdpr",
  "ccpa",
  "soc2",
  "iso27001",
  "eu_data_residency",
  "built_on_hetzner",
  "built_on_cloudflare",
  "payment_cards_pci",
  "money_back_guarantee",
] as const;

export type TrustBadgeId = (typeof TRUST_BADGE_IDS)[number];

/** Region a badge applies to. `global` shows in every region. */
export type TrustBadgeRegion = "global" | "eu" | "us" | "uk" | "ca";

/**
 * Static metadata for a badge, locale-resolved by the i18n layer at render
 * time (Phase 3 in the packet). `localeRewrites` lets the resolver swap
 * the canonical English label for a regulated synonym when the active
 * locale is configured for it — e.g. `gdpr` renders as "RGPD" under
 * `fr-*` so French/Belgian/Canadian tenants comply with local regulator
 * naming conventions.
 */
export interface TrustBadgeDefinition {
  readonly id: TrustBadgeId;
  readonly defaultLabel: string;
  /** Regions where this badge is meaningful. `global` = show everywhere. */
  readonly regions: readonly TrustBadgeRegion[];
  /** Optional locale-prefix → label overrides ("fr" → "RGPD"). */
  readonly localeRewrites?: Readonly<Record<string, string>>;
}

/**
 * Canonical badge catalog. The label here is the source of truth for the
 * default English render; locale-aware rewrites are declared inline so a
 * single grep ("RGPD") surfaces the contract.
 *
 * Regions are encoded conservatively: `gdpr` only fires for EU + UK,
 * `ccpa` only fires for US, `iso27001` / `soc2` / `eu_data_residency`
 * are tied to compliance posture rather than region (set to `global` so
 * any regulated tenant can opt in via the per-tenant config layer added
 * in Phase 2 of the packet).
 */
export const TRUST_BADGE_CATALOG: Readonly<Record<TrustBadgeId, TrustBadgeDefinition>> = {
  gdpr: {
    id: "gdpr",
    defaultLabel: "GDPR compliant",
    regions: ["eu", "uk"],
    localeRewrites: { fr: "RGPD conforme", de: "DSGVO-konform" },
  },
  ccpa: {
    id: "ccpa",
    defaultLabel: "CCPA compliant",
    regions: ["us"],
  },
  soc2: {
    id: "soc2",
    defaultLabel: "SOC 2 mapped",
    regions: ["global"],
  },
  iso27001: {
    id: "iso27001",
    defaultLabel: "ISO 27001 aligned",
    regions: ["global"],
  },
  eu_data_residency: {
    id: "eu_data_residency",
    defaultLabel: "EU data residency",
    regions: ["eu", "uk"],
    localeRewrites: { fr: "Données hébergées en UE", de: "EU-Datenresidenz" },
  },
  built_on_hetzner: {
    id: "built_on_hetzner",
    defaultLabel: "Hosted on Hetzner (EU)",
    regions: ["global"],
  },
  built_on_cloudflare: {
    id: "built_on_cloudflare",
    defaultLabel: "Powered by Cloudflare",
    regions: ["global"],
  },
  payment_cards_pci: {
    id: "payment_cards_pci",
    defaultLabel: "PCI-DSS via Stripe",
    regions: ["global"],
  },
  money_back_guarantee: {
    id: "money_back_guarantee",
    defaultLabel: "30-day money-back guarantee",
    regions: ["global"],
  },
};

// ---------------------------------------------------------------------------
// Per-tenant config (Phase 2 shape)
// ---------------------------------------------------------------------------

/**
 * Zod schema for the `tenants.trust_badge_config` jsonb column promised
 * by the decision packet §"Minimal viable scope" Phase 2. Kept here (not
 * in `@ease/db`) so workers without a Drizzle dependency can validate
 * incoming config from the control-plane PUT route.
 */
export const TrustBadgeConfigSchema = z.object({
  /** Badges explicitly enabled. Empty/missing = use platform defaults. */
  enabledBadges: z.array(z.enum(TRUST_BADGE_IDS)).default([]),
  /** Badges the tenant has chosen to hide even if region/default would show them. */
  hiddenBadges: z.array(z.enum(TRUST_BADGE_IDS)).default([]),
});

export type TrustBadgeConfig = z.infer<typeof TrustBadgeConfigSchema>;

const EMPTY_TRUST_BADGE_IDS = Object.freeze([]) as unknown as TrustBadgeId[];

export const EMPTY_TRUST_BADGE_CONFIG: TrustBadgeConfig = Object.freeze({
  enabledBadges: EMPTY_TRUST_BADGE_IDS,
  hiddenBadges: EMPTY_TRUST_BADGE_IDS,
});

// ---------------------------------------------------------------------------
// Render-side payload
// ---------------------------------------------------------------------------

/**
 * The shape `<TrustBadges>` (Phase 1) and the Puck `trust-badges` Page
 * Builder component (Phase 4) consume. Pre-resolved so no fetching,
 * locale logic, or region filtering happens in render-land.
 */
export interface ResolvedTrustBadge {
  readonly id: TrustBadgeId;
  readonly label: string;
  /** ISO-639 language tag that produced the label (for `lang=`). */
  readonly languageTag: string;
}

export interface ResolveTrustBadgesInput {
  /** Per-tenant override row. `null` / `undefined` treated as empty. */
  readonly tenantConfig?: TrustBadgeConfig | null;
  /** BCP-47 active locale ("en-US", "fr-FR", "de-DE"). */
  readonly locale: string;
  /** Tenant's data-residency / region marker. */
  readonly region: TrustBadgeRegion;
}

// ---------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------

function normalizeRegion(value: string | null | undefined): TrustBadgeRegion {
  if (value === "eu" || value === "us" || value === "uk" || value === "ca") {
    return value;
  }
  return "global";
}

function pickLocaleLabel(
  definition: TrustBadgeDefinition,
  locale: string,
): { label: string; languageTag: string } {
  const primary = locale.toLowerCase().split(/[-_]/)[0] ?? "";
  const override = definition.localeRewrites?.[primary];
  if (override) {
    return { label: override, languageTag: primary };
  }
  return { label: definition.defaultLabel, languageTag: "en" };
}

/**
 * Resolve the visible badge set for a tenant. Pure function — no I/O,
 * no clock, no DB. Order is deterministic (catalog declaration order)
 * so snapshot tests are stable.
 *
 * Filter pipeline (decision packet §"Cannot test-prove the four UX-020
 * invariants" mapped to runtime):
 *
 * 1. Start with the tenant's `enabledBadges` set (explicit opt-in) and
 *    apply locale rewrites.
 * 2. Drop any badge in `hiddenBadges` (explicit opt-out wins).
 * 3. Drop any badge whose `regions` do not include the tenant region
 *    (unless `global`, which always passes).
 *
 * Returns an empty array when no badges resolve — that is a valid render
 * state ("no trust strip"), not an error.
 */
export function resolveTrustBadges(input: ResolveTrustBadgesInput): readonly ResolvedTrustBadge[] {
  const config = input.tenantConfig ?? EMPTY_TRUST_BADGE_CONFIG;
  const region = normalizeRegion(input.region);
  const hidden = new Set<TrustBadgeId>(config.hiddenBadges);
  const enabled = new Set<TrustBadgeId>(config.enabledBadges);

  const resolved: ResolvedTrustBadge[] = [];
  for (const id of TRUST_BADGE_IDS) {
    if (!enabled.has(id)) continue;
    if (hidden.has(id)) continue;
    const definition = TRUST_BADGE_CATALOG[id];
    const regionAllowed = definition.regions.includes(region)
      || definition.regions.includes("global");
    if (!regionAllowed) continue;
    const { label, languageTag } = pickLocaleLabel(definition, input.locale);
    resolved.push({ id, label, languageTag });
  }
  return resolved;
}
